import { useCallback, useMemo, useState } from 'react';
import { ColumnsType, ColumnType } from 'antd/lib/table';

/** A column-visibility preference key. Mirrors antd's `React.Key`. */
export type ColumnKey = string | number;

/** Persisted shape: a blocklist of *hidden* column keys, versioned for migration. */
export interface StoredColumnPrefs {
	v: number;
	hidden: string[];
}

const STORAGE_PREFIX = 'HorusEye.table';
// Bump when the column set changes in a way that should reset users' saved
// show/hide choices (stored prefs of an older version are ignored -> defaults apply).
const CURRENT_VERSION = 4;

export const columnPrefsStorageKey = (storageKey: string): string => `${STORAGE_PREFIX}.${storageKey}.columns`;

/**
 * Read the persisted hidden-key blocklist. Returns `null` when nothing valid is
 * stored (missing, malformed, or a version we don't understand) so callers can
 * fall back to their defaults.
 */
export function loadHiddenKeys(storageKey: string): string[] | null {
	try {
		const raw = localStorage.getItem(columnPrefsStorageKey(storageKey));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<StoredColumnPrefs>;
		if (parsed && parsed.v === CURRENT_VERSION && Array.isArray(parsed.hidden)) {
			return parsed.hidden.filter((k): k is string => typeof k === 'string');
		}
		return null;
	} catch {
		return null;
	}
}

/** Persist the hidden-key blocklist. Silent no-op if storage is unavailable. */
export function saveHiddenKeys(storageKey: string, hidden: string[]): void {
	try {
		const payload: StoredColumnPrefs = { v: CURRENT_VERSION, hidden };
		localStorage.setItem(columnPrefsStorageKey(storageKey), JSON.stringify(payload));
	} catch {
		/* ignore quota errors / unavailable storage */
	}
}

/** Identify a column by its `key`, falling back to a scalar `dataIndex`. */
export function columnKey<T>(col: ColumnType<T>): string | undefined {
	if (col.key != null) return String(col.key);
	const di = col.dataIndex;
	if (typeof di === 'string' || typeof di === 'number') return String(di);
	return undefined;
}

export interface ColumnVisibilityConfig {
	/** Namespace for localStorage persistence. Omit to disable persistence. */
	storageKey?: string;
	/** Columns hidden on first load (before the user has expressed a preference). */
	defaultHidden?: ColumnKey[];
	/** Columns the user can never hide (rendered checked + disabled in the selector). */
	alwaysVisible?: ColumnKey[];
}

/**
 * Compute the initial hidden set: stored preferences win over `defaultHidden`;
 * an `alwaysVisible` column is never hidden regardless of what is stored.
 */
export function computeInitialHidden(config: ColumnVisibilityConfig): Set<string> {
	const { storageKey, defaultHidden = [], alwaysVisible = [] } = config;
	const stored = storageKey ? loadHiddenKeys(storageKey) : null;
	const base = stored ?? defaultHidden.map(String);
	const always = new Set(alwaysVisible.map(String));
	return new Set(base.map(String).filter(k => !always.has(k)));
}

/**
 * Drop hidden columns. Unknown/stale keys in `hidden` are ignored (that is what
 * makes the blocklist robust across column changes); columns without an
 * identifiable key are always kept.
 */
export function filterVisibleColumns<T>(columns: ColumnsType<T>, hidden: Set<string>): ColumnsType<T> {
	return columns.filter(col => {
		const k = columnKey(col as ColumnType<T>);
		return k == null || !hidden.has(k);
	});
}

export interface ColumnVisibility {
	hidden: Set<string>;
	isHidden: (key: ColumnKey) => boolean;
	setHidden: (key: ColumnKey, hidden: boolean) => void;
	alwaysVisible: Set<string>;
}

/**
 * React state for column visibility, persisted to localStorage as a hidden-key
 * blocklist. Seeded once from stored prefs / `defaultHidden`; every change is
 * written straight back to storage.
 */
export function useColumnVisibility(config: ColumnVisibilityConfig): ColumnVisibility {
	const { storageKey, defaultHidden, alwaysVisible } = config;

	const alwaysVisibleSet = useMemo(() => new Set((alwaysVisible ?? []).map(String)), [alwaysVisible]);

	const [hidden, setHiddenState] = useState<Set<string>>(() =>
		computeInitialHidden({ storageKey, defaultHidden, alwaysVisible }),
	);

	const setHidden = useCallback(
		(key: ColumnKey, hide: boolean) => {
			const k = String(key);
			if (alwaysVisibleSet.has(k)) return; // an always-visible column can never be hidden
			setHiddenState(prev => {
				if (hide === prev.has(k)) return prev;
				const next = new Set(prev);
				if (hide) next.add(k);
				else next.delete(k);
				if (storageKey) saveHiddenKeys(storageKey, Array.from(next));
				return next;
			});
		},
		[alwaysVisibleSet, storageKey],
	);

	const isHidden = useCallback((key: ColumnKey) => hidden.has(String(key)), [hidden]);

	return { hidden, isHidden, setHidden, alwaysVisible: alwaysVisibleSet };
}
